import { Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import {
    BookingDetailsModel,
    BookingFilterInfoModel,
    BookingInfoSelectedFiltersModel,
    MaskedTimeslotPlaceholdersResponse,
    TimeSlotResultModel,
} from '../../model';
import { ConfirmationSlotData, SelfBookingStateModel } from './self-booking-state.model';
import { SelfBookingActions } from './self-booking.actions';

const SELF_BOOK_STATE_TOKEN: StateToken<SelfBookingStateModel> = new StateToken('SelfBookState');

const DEFAULT_STATE: SelfBookingStateModel = {
    bookingInfo: { state: ObjectState.UNINIT, value: null },
    bookingFilterInfo: { state: ObjectState.UNINIT, value: null },
    bookingFilterPlaceholders: { state: ObjectState.UNINIT, value: null },
    timeslotResult: { state: ObjectState.UNINIT, value: null },
    selectedFilterUuids: undefined,
    selectedFromDate: undefined,
    selectedToDate: undefined,
    lastBookedAllowanceToken: undefined,
    loadSlotPreview: undefined,
    confirmationSlot: undefined,
};

@State<SelfBookingStateModel>({
    name: SELF_BOOK_STATE_TOKEN,
    defaults: DEFAULT_STATE,
    children: [],
})
@Injectable()
export class SelfBookingState {
    constructor() {}

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static bookingInfoBsServiceName$(state: SelfBookingStateModel): StatefullValue<string | null> {
        console.warn(state.bookingInfo.value);
        const serviceName = state.bookingInfo?.value?.bookingAllowanceinfo?.service?.bsServices?.[0]?.name ?? null;
        return {
            state: state.bookingInfo.state,
            stateErrorCode: state.bookingInfo.stateErrorCode,
            value: serviceName,
        };
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static selectedFilterUuids$(state: SelfBookingStateModel): BookingInfoSelectedFiltersModel | undefined {
        return state.selectedFilterUuids;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static bookingInfo$(state: SelfBookingStateModel): StatefullValue<BookingDetailsModel | null> {
        return state.bookingInfo;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static bookingFilterInfo$(state: SelfBookingStateModel): StatefullValue<BookingFilterInfoModel | null> {
        return state.bookingFilterInfo;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static bookingFilterPlaceholders$(state: SelfBookingStateModel): StatefullValue<MaskedTimeslotPlaceholdersResponse | null> {
        return state.bookingFilterPlaceholders;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static timeslotResult$(state: SelfBookingStateModel): StatefullValue<TimeSlotResultModel[] | null> {
        return state.timeslotResult;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static loadSlotPreview$(state: SelfBookingStateModel): number | undefined {
        return state.loadSlotPreview;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static lastBookedAllowanceToken$(state: SelfBookingStateModel): string | undefined {
        return state.lastBookedAllowanceToken;
    }

    @Selector([SELF_BOOK_STATE_TOKEN])
    public static confirmationSlot$(state: SelfBookingStateModel): ConfirmationSlotData | undefined {
        return state.confirmationSlot;
    }

    @Action(SelfBookingActions.SetBookingDetails)
    public setBookingDetails$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetBookingDetails): void {
        ctx.patchState({
            bookingInfo: action.bookingDetails,
        });
    }

    @Action(SelfBookingActions.SetBookingFilterInfo)
    public SetBookingFilterInfo$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetBookingFilterInfo): void {
        ctx.patchState({
            bookingFilterInfo: action.bookingfilter,
        });
    }

    @Action(SelfBookingActions.SetBookingFilters)
    public setAuthState$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetBookingFilters): void {
        ctx.patchState({
            selectedFilterUuids: action.selectedFilterUuids,
        });
    }

    @Action(SelfBookingActions.SetLoadSlotsPreview)
    public setLoadSlotsPreview$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetLoadSlotsPreview): void {
        ctx.patchState({
            loadSlotPreview: action.loadSlotsCombinationsCount,
        });
    }

    @Action(SelfBookingActions.SetTimeslotResult)
    public setTimeslots$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetTimeslotResult): void {
        ctx.patchState({
            timeslotResult: action.timeslotResult,
        });
    }

    @Action(SelfBookingActions.SetLastBookedAllowanceToken)
    public setLastBookedAllowanceToken$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetLastBookedAllowanceToken): void {
        ctx.patchState({
            lastBookedAllowanceToken: action.lastBookedAllowanceToken,
        });
    }

    @Action(SelfBookingActions.SetBookingFilterPlaceholders)
    public setBookingFilterPlaceholders$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetBookingFilterPlaceholders): void {
        ctx.patchState({
            bookingFilterPlaceholders: action.bookingFilterPlaceholders,
        });
    }

    @Action(SelfBookingActions.SetConfirmationSlot)
    public setConfirmationSlot$(ctx: StateContext<SelfBookingStateModel>, action: SelfBookingActions.SetConfirmationSlot): void {
        ctx.patchState({
            confirmationSlot: {
                slot: action.slot,
                metadata: action.metadata,
            },
        });
    }

    @Action(SelfBookingActions.ClearConfirmationSlot)
    public clearConfirmationSlot$(ctx: StateContext<SelfBookingStateModel>): void {
        ctx.patchState({
            confirmationSlot: undefined,
        });
    }

    @Action(SelfBookingActions.ResetState)
    public resetState$(ctx: StateContext<SelfBookingStateModel>): void {
        ctx.setState(DEFAULT_STATE);
    }
}
